import type { CellValue, Color } from 'exceljs'

/**
 * Drawing a cell the way Excel would: its value as Excel displays it under the
 * cell's number format, and its colours as CSS.
 *
 * This covers the formats our workbooks actually carry — the server's
 * `#,##0.00;-#,##0.00;"-"`, `0.0%`, `dd-mmm-yyyy hh:mm` and `General` — and the
 * common ones a merchant would meet anywhere else. Exotic formats (fractions,
 * scientific, conditions) fall back to General rather than print nonsense.
 */

export type PlainValue = string | number | boolean | Date | null

/** A cell's value with exceljs's wrappers (rich text, formulas, links) taken off. */
export function plainValue(value: CellValue | undefined): PlainValue {
  if (value === null || value === undefined) return null
  if (value instanceof Date || typeof value !== 'object') return value

  if ('richText' in value) return value.richText.map((run) => run.text).join('')
  if ('formula' in value || 'sharedFormula' in value) {
    const { result } = value as { result?: CellValue }
    return result === undefined ? null : plainValue(result)
  }
  if ('hyperlink' in value) return String(value.text ?? value.hyperlink)
  if ('error' in value) return String(value.error)

  return null
}

/** What the formula bar shows: the formula when there is one, otherwise the value. */
export function formulaBarText(value: CellValue | undefined, numFmt?: string): string {
  if (value && typeof value === 'object' && !(value instanceof Date) && 'formula' in value) {
    return `=${value.formula}`
  }

  const plain = plainValue(value)
  if (plain instanceof Date) return formatDate(plain, isDateFormat(numFmt) ? numFmt! : 'yyyy-mm-dd hh:mm')
  if (typeof plain === 'number') return String(plain)

  return displayText(plain, numFmt)
}

export function displayText(value: PlainValue, numFmt?: string): string {
  if (value === null) return ''
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (value instanceof Date) return formatDate(value, isDateFormat(numFmt) ? numFmt! : defaultDateFormat(value))
  if (typeof value === 'number') return formatNumber(value, numFmt)

  return value
}

/* -------------------------------------------------------------------------- */
/* Format sections                                                            */
/* -------------------------------------------------------------------------- */

/** `pos;neg;zero;text`, split on the semicolons outside quotes. */
function sections(format: string): string[] {
  const parts: string[] = []
  let current = ''
  let quoted = false

  for (let i = 0; i < format.length; i++) {
    const char = format[i]

    if (char === '"') quoted = !quoted
    if (char === '\\' && !quoted) {
      current += char + (format[i + 1] ?? '')
      i++
      continue
    }
    if (char === ';' && !quoted) {
      parts.push(current)
      current = ''
      continue
    }
    current += char
  }

  parts.push(current)
  return parts
}

/** The format with its quoted text, escapes, colours and padding removed. */
function bare(section: string): string {
  return section
    .replace(/"[^"]*"/g, '')
    .replace(/\\./g, '')
    .replace(/\[[^\]]*\]/g, '')
    .replace(/[_*]./g, '')
}

export function isDateFormat(format: string | undefined): boolean {
  if (!format) return false
  const stripped = bare(sections(format)[0])

  return /[ymdhs]/i.test(stripped) && !/[0#?]/.test(stripped.replace(/s+\.0+/i, 's'))
}

/* -------------------------------------------------------------------------- */
/* Numbers                                                                    */
/* -------------------------------------------------------------------------- */

function general(value: number): string {
  if (Number.isInteger(value)) return String(value)

  return String(Number.parseFloat(value.toPrecision(10)))
}

export function formatNumber(value: number, format: string | undefined): string {
  if (!format || /^general$/i.test(format.trim())) return general(value)

  const parts = sections(format)
  let section = parts[0]
  let shown = value
  let sign = ''

  if (value < 0 && parts[1] !== undefined && parts[1] !== '') {
    section = parts[1]
    shown = Math.abs(value)
  } else if (value === 0 && parts[2] !== undefined) {
    section = parts[2]
  } else if (value < 0) {
    sign = '-'
    shown = Math.abs(value)
  }

  if (/^general$/i.test(bare(section).trim())) return sign + general(shown)

  // Walk the section once: literal text passes through, the first run of digit
  // placeholders becomes the number.
  let out = ''
  let pattern = ''
  let placed = false

  for (let i = 0; i < section.length; i++) {
    const char = section[i]

    if (char === '"') {
      const end = section.indexOf('"', i + 1)
      out += section.slice(i + 1, end === -1 ? undefined : end)
      i = end === -1 ? section.length : end
    } else if (char === '\\') {
      out += section[i + 1] ?? ''
      i++
    } else if (char === '_') {
      out += ' '
      i++
    } else if (char === '*') {
      i++
    } else if (char === '[') {
      const end = section.indexOf(']', i)
      i = end === -1 ? section.length : end
    } else if (/[0#?,.]/.test(char) && !placed) {
      pattern += char
      if (!/[0#?,.]/.test(section[i + 1] ?? '')) {
        out += '\u0000'
        placed = true
      }
    } else {
      out += char
    }
  }

  if (!pattern) return out

  const percent = (section.match(/%/g)?.length ?? 0) > 0
  const [integerPart, decimalPart = ''] = pattern.split('.')
  const maxDecimals = decimalPart.replace(/[^0#?]/g, '').length
  const minDecimals = decimalPart.replace(/[^0]/g, '').length
  const number = (percent ? shown * 100 : shown).toLocaleString('en-US', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
    useGrouping: integerPart.includes(','),
  })

  return sign + out.replace('\u0000', number)
}

/* -------------------------------------------------------------------------- */
/* Dates                                                                      */
/* -------------------------------------------------------------------------- */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function defaultDateFormat(date: Date): string {
  const hasTime = date.getUTCHours() + date.getUTCMinutes() + date.getUTCSeconds() > 0

  return hasTime ? 'yyyy-mm-dd hh:mm' : 'yyyy-mm-dd'
}

const pad = (value: number, length = 2) => String(value).padStart(length, '0')

/**
 * Excel dates carry no time zone: exceljs hands them back as the UTC instant
 * with the same wall-clock reading, so they are read with the UTC getters.
 */
export function formatDate(date: Date, format: string): string {
  const section = sections(format)[0]
  const tokens = section.match(/"[^"]*"|\\.|\[[^\]]*\]|yyyy|yy|mmmmm|mmmm|mmm|mm|m|dddd|ddd|dd|d|hh|h|ss|s|AM\/PM|am\/pm|A\/P|a\/p|./gi) ?? []
  const twelveHour = tokens.some((token) => /^(am\/pm|a\/p)$/i.test(token))
  const isTime = (token: string | undefined) => token !== undefined && /^(h+|s+)$/i.test(token)
  const hours = date.getUTCHours()

  return tokens
    .map((token, index) => {
      const lower = token.toLowerCase()
      const previous = tokens.slice(0, index).reverse().find((t) => /^[a-z]/i.test(t))
      const next = tokens.slice(index + 1).find((t) => /^[a-z]/i.test(t))

      switch (lower) {
        case 'yyyy':
          return String(date.getUTCFullYear())
        case 'yy':
          return pad(date.getUTCFullYear() % 100)
        case 'mmmmm':
          return MONTHS[date.getUTCMonth()][0]
        case 'mmmm':
          return MONTHS[date.getUTCMonth()]
        case 'mmm':
          return MONTHS[date.getUTCMonth()].slice(0, 3)
        case 'mm':
        case 'm': {
          // "m" after an hour or before a second is minutes, not the month.
          const minutes = isTime(previous) || isTime(next)
          const n = minutes ? date.getUTCMinutes() : date.getUTCMonth() + 1
          return lower === 'mm' ? pad(n) : String(n)
        }
        case 'dddd':
          return DAYS[date.getUTCDay()]
        case 'ddd':
          return DAYS[date.getUTCDay()].slice(0, 3)
        case 'dd':
          return pad(date.getUTCDate())
        case 'd':
          return String(date.getUTCDate())
        case 'hh':
        case 'h': {
          const n = twelveHour ? hours % 12 || 12 : hours
          return lower === 'hh' ? pad(n) : String(n)
        }
        case 'ss':
          return pad(date.getUTCSeconds())
        case 's':
          return String(date.getUTCSeconds())
        case 'am/pm':
          return hours < 12 ? 'AM' : 'PM'
        case 'a/p':
          return hours < 12 ? 'A' : 'P'
      }

      if (token.startsWith('"')) return token.slice(1, -1)
      if (token.startsWith('\\')) return token.slice(1)
      if (token.startsWith('[')) return ''
      return token
    })
    .join('')
}

/* -------------------------------------------------------------------------- */
/* Colours                                                                    */
/* -------------------------------------------------------------------------- */

/** The default Office theme, in theme-index order. */
const THEME = ['FFFFFF', '000000', 'E7E6E6', '44546A', '4472C4', 'ED7D31', 'A5A5A5', 'FFC000', '5B9BD5', '70AD47']

function tinted(hex: string, tint: number): string {
  const channels = [0, 2, 4].map((i) => Number.parseInt(hex.slice(i, i + 2), 16))
  const shifted = channels.map((c) => Math.round(tint < 0 ? c * (1 + tint) : c + (255 - c) * tint))

  return shifted.map((c) => Math.min(255, Math.max(0, c)).toString(16).padStart(2, "0")).join("")
}

export function cssColor(color: Partial<Color> | undefined): string | undefined {
  if (!color) return undefined
  if (color.argb) return `#${color.argb.slice(-6)}`
  if (color.theme !== undefined && THEME[color.theme]) {
    const { tint = 0 } = color as { tint?: number }
    return `#${tint ? tinted(THEME[color.theme], tint) : THEME[color.theme]}`
  }

  return undefined
}

/* -------------------------------------------------------------------------- */
/* Addresses                                                                  */
/* -------------------------------------------------------------------------- */

export function columnLetter(column: number): string {
  let letters = ''
  for (let n = column; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters
  }
  return letters
}

/** `B7` → `{ row: 7, column: 2 }`. */
export function decodeAddress(address: string): { row: number; column: number } {
  const match = /^\$?([A-Z]+)\$?(\d+)$/i.exec(address.trim())
  if (!match) return { row: 0, column: 0 }

  const column = [...match[1].toUpperCase()].reduce((n, char) => n * 26 + char.charCodeAt(0) - 64, 0)
  return { row: Number(match[2]), column }
}

/** `A1:D3` → its corners. */
export function decodeRange(range: string) {
  const [start, end = start] = range.split(':')
  const a = decodeAddress(start)
  const b = decodeAddress(end)

  return {
    top: Math.min(a.row, b.row),
    left: Math.min(a.column, b.column),
    bottom: Math.max(a.row, b.row),
    right: Math.max(a.column, b.column),
  }
}
